<template>
  <card :title="title" id="details" v-if="canEdit || canDelete" v-cloak>
    <div class="row" v-if="false">
      <div class="form-group col-sm-12 col-lg-3 text-left">
        <label class="control-label">{{ $t("scale.weighing_on_scale.contract_number") }}</label>
        <div class="sm-select-input-dialog-btn" @click="contractClick">
          <div class="sm-select-input-dialog-info">
            <div class="input_value">{{ getContractNames }}</div>
          </div>
          <div class="sm-select-input-dialog-actions">
            <span
              v-if="selectedContracts && selectedContracts.length > 0"
              class="sm-select-input-dialog-clear"
              @click.stop="clearSelectedContracts"
              :title="$t('common.delete')"
            >
              <i class="fa fa-times"></i>
            </span>
            <span class="sm-select-input-dialog-icon">
              <i class="fa-regular fa-edit"></i>
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-3 text-left">
        <label class="control-label">{{
          $t("stock_management.sales.date")
        }}</label>
        <date-picker2
          v-model="data.sales_date"
          :value-type="sales_date_input"
          :format="getDefaultDateFormat"
          type="date"
          :input-class="['date-picker-style']"
          :class="{ 'field-error': this.$v.data.sales_date.$error }"
          ref="sales_date"
          :disabled="(!!data.batch_uuid && !!data.sales_date)"
        ></date-picker2>
      </div>
    </div>

    <div class="row">
      <div class="form-group col-sm-12 col-lg-3 text-left">
        <label class="control-label">{{
          $t("common.product_name")
        }}</label>
        <v-select
          v-model="productNameSearch"
          :class="{ 'field-error': this.$v.data.product.name.$error }"
          :placeholder="$t('stock_management.products.search_by_name')"
          autocomplete="off"
          @option:selected="onProductNameSelected"
          ref="product_name"
          :options="product_name_list"
          :disabled="selectedContracts?.length > 0"
        ></v-select>
      </div>
      <div class="col-sm-12 col-lg-3 form-group">
        <label class="control-label">{{ $t("common.warehouse") }}</label>
        <b-form-select
          v-model="data.warehouse_uuid"
          class="form-control"
          :class="{ 'field-error': $v.data.warehouse_uuid.$error }"
          :options="warehouse_list"
          :disabled="!!data.batch_uuid"
          @change="onWarehouseChanged"
        ></b-form-select>
      </div>
    </div>

    <div class="row">
      <div class="form-group col-sm-12 col-lg-3 text-left">
        <label class="control-label">
          <span>{{ $t("common.quantity") }}</span>
        </label>
        <span
          class="font-small text-primary"
          v-if="productNameSearch && productNameSearch.value"
        >
          {{ $t("stock_management.products.available_quantity") }}:
          {{ this.getStockCount(this.data.warehouse_uuid) | getFormattedQuantity }}
          {{ data.product.measure_unit.name }}
        </span>
        <span class="mu"
          >{{ data.product.measure_unit ? data.product.measure_unit.name : "" }}
        </span>
        <DxNumberBox
          v-model="data.product.quantity"
          :min="0"
          :showSpinButtons="false"
          class="dx-form-control"
          :class="{ 'field-error': $v.data.product.quantity.$error }"
          :format="quantityFormatter"
          placeholder="0.00"
        />
        <span class="text-danger small-font">{{ maxQtyError }}</span>
      </div>
      <div class="form-group col-sm-12 col-lg-3 text-left">
        <label class="control-label">
          <span>{{ $t("common.unit_price") }}</span>
          <span
            class="fyi-price text-primary"
            v-if="
              data.product.uuid &&
              stock_product_info &&
              stock_product_info.price_net > 0
            "
          >
            {{
              $t("stock_management.sales.stored_price", {
                price:
                  stock_product_info.price_net.toFixed(2) +
                  " " +
                  $currency.name,
              })
            }}
          </span>
        </label>
        <input
          v-model="data.product.unit_price"
          class="form-control upper"
          :class="{ 'field-error': $v.data.product.unit_price.$error }"
          v-on:keydown="onlyPrice"
          placeholder="0.00"
        />
      </div>
    </div>

    <div class="row">
      <div class="col-sm-6 col-lg-6 form-group">
        <label class="control-label">{{
          $t("stock_management.sales.client")
        }}</label>
        <autocomplete-search
          :placeholder="$t('stock_management.sales.client_search')"
          :dispatch="getPartners"
          search-key="name"
          ref="partner"
          :required="true"
          :data="partnerName"
          @onSelected="onPartnerSelected"
          @onChange="onPartnerChanged"
          :disabled="selectedContracts?.length > 0"
        ></autocomplete-search>
        <img
          src="~@/assets/images/desktop/icons/controls/plus.svg"
          class="icon-svg float-right text-success c-pointer"
          @click="showPartnerDialog"
          v-if="addNewPartner === 'add'"
        />
        <img
          src="~@/assets/images/desktop/icons/controls/edit.svg"
          class="icon-svg float-right text-success c-pointer"
          @click="showPartnerDialog"
          v-if="addNewPartner === 'edit'"
        />
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-6 text-left">
        <label class="control-label">{{ $t("common.notes") }}</label>
        <textarea
          v-model="data.notes"
          class="form-control textarea-2-lines"
          :maxlength="185"
        ></textarea>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-6">
        <label class="control-label font-weight-bold">{{
          $t("common.total")
        }}</label>
        <div>{{ total + " " + $currency.name }}</div>
      </div>
    </div>

    <agro-action-buttons
      v-if="!saveButtonPressed"
      :data="data"
      :can-edit="canEdit"
      :can-delete="canDelete"
      @onSave="save"
      @onSaveAndNew="saveAndNew"
      :save-and-new="true"
      @onDelete="confirmDelete"
      @onGotoList="gotoList"
    ></agro-action-buttons>

    <CreatePartnerDialog
      :show="showPartnersModal"
      @close="showPartnersModal = false"
      :data="partnersModalData"
      @onOK="onPartnerDialogClosed"
    ></CreatePartnerDialog>

    <ContractsPurchaseSaleSelector
      v-if="showContractSelectDialog"
      :visible="showContractSelectDialog"
      :selectedContractUuids="selectedContracts?.map((contract) => contract.uuid) ?? []"
      @close="showContractSelectDialog = false"
      @continue="onContinueFromContractSelector"
      :multiselect="false"
      :filterData="{
        contract_type_id: '2',
        show_completed_rows: 0,
        sale_type: 'partner',
      }"
    />
  </card>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import {
  buildDefaultAutocompleteList,
  buildDefaultDropdownList,
  errorMessage,
  only_price,
} from "@/helpers/common";
import required from "vuelidate/src/validators/required";
import {
  canDelete,
  canEdit,
  permissions,
  permissionTypes,
} from "@/helpers/Permissions";
import * as actions from "@/helpers/generalActions";
import AgroActionButtons from "@/components/AgroActionButtons/index";
import Forbidden from "@/pages/errors/Forbidden";
import VueBootstrapTypeahead from "vue-bootstrap-typeahead";
import AutocompleteSearch from "~/components/Common/AutocompleteSearch/Index.vue";
import CreatePartnerDialog from "~/pages/stock-management/sm-partners/partials/CreatePartnerDialog.vue";
import DatePicker2 from "vue2-datepicker";
import moment from "moment";
import DxNumberBox from "devextreme-vue/number-box";
import { getQuantityFormatter } from '~/helpers/stockHelper';
import ContractsPurchaseSaleSelector from "@/components/ContractsPurchaseSaleSelector.vue";

export default {
  name: "Sales",
  components: {
    CreatePartnerDialog,
    AutocompleteSearch,
    Forbidden,
    AgroActionButtons,
    VueBootstrapTypeahead,
    DatePicker2,
    DxNumberBox,
    ContractsPurchaseSaleSelector,
  },
  data() {
    return {
      data: { 
        product: {}, 
        sales_date: null,
        batch_uuid: null,
      },
      product_types: this.productTypes ? this.productTypes : [],
      productBarcodeList: [],
      productBarcodeSearch: "",
      selectedProductBarcode: null,
      productNameList: [],
      productNameSearch: "",
      selectedProductName: null,
      productList: [],
      addNewPartner: false,
      showPartnersModal: false,
      partnersModalData: {},
      maxQtyError: "",
      stock_product_info: null,
      partnerInfo: null,
      sales_date_input: null,
      saveButtonPressed: false,
      showContractSelectDialog: false,
      selectedContracts: [],
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      users: "user/users",
      domain: "auth/domain",
      products: "smProducts/smProducts",
      productTypes: "smProductTypes/smProductTypes",
      warehouses: "smWarehouses/smWarehouses",
      partners: "smPartners/smPartners",
      stockProductInfo: "smStocks/smStockProductInfo",
      getDefaultDateFormat: 'settings/getDefaultDateFormat',
    }),
    quantityFormatter(){
      return getQuantityFormatter();
    },
    name() {
      return this.$t("stock_management.sales.title");
    },
    config() {
      return {
        permission: permissions.SM_STOCK,
        permission_type: permissionTypes.module,
        get: this.get,
        create: this.create,
        list_url: "/stock-management/sm-stocks",
      };
    },
    actions() {
      return actions;
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    title() {
      return this.name;
    },
    product_barcode_list() {
      return buildDefaultAutocompleteList(this.products, {
        uuid: "uuid",
        name: "barcode",
      });
    },
    product_name_list() {
      return buildDefaultAutocompleteList(this.products);
    },
    warehouse_list() {
      return buildDefaultDropdownList(Object.assign([], this.warehouses));
    },
    productBarcode() {
      return {
        uuid: this.data.product?.uuid,
        name: this.data.product?.barcode,
      };
    },
    productName() {
      return {
        uuid: this.data.product?.uuid,
        name: this.data.product?.name,
      };
    },
    partnerName: {
      get() {
        return {
          uuid: this.data.product?.partner?.uuid,
          name: this.data.product?.partner?.name,
        };
      },
      set(val) {
        return {
          uuid: val.uuid,
          name: val.name,
        };
      },
    },
    total() {
      if (this.data.product.quantity > 0 && this.data.product.unit_price > 0) {
        return (
          this.data.product.quantity * this.data.product.unit_price
        ).toFixed(2);
      }

      return (0).toFixed(2);
    },
    getContractNames() {
      if (!this.selectedContracts || this.selectedContracts.length === 0) {
        return " - ";
      }
      return this.selectedContracts.map((l) => l.contract_number).join(", ");
    },
  },
  methods: {
    ...mapActions({
      getProductTypes: "smProductTypes/searchSmProductTypes",
      get: "smProducts/getSmProduct",
      create: "smTransactions/createSalesTransaction",
      getProducts: "smProducts/getSmProducts",
      getWarehouses: "smWarehouses/getSmWarehouses",
      getPartners: "smPartners/getSmPartners",
      getStockProductInfo: "smStocks/getSmStockProductInfo",
    }),
    loadData() {
      let vm = this;
      return this.actions.getData(
        this,
        (data) => {
          vm.$set(vm.data, "warehouse_uuid", null);
        },
        (data) => {}
      );
    },
    gotoList() {
      return actions.gotoList(this);
    },
    confirmDelete() {
      return this.actions.confirmDelete(this);
    },
    prepareSave(callback, context, backPath) {
      this.$v.data.$touch();

      this.data.product.partner = this.partnerInfo;
      let partnerValidate = this.$refs["partner"].validate();
      this.data.product.price = this.total;
      let whs = this.warehouses.find(
        (w) => w.uuid === this.data.warehouse_uuid
      );

      if (whs) {
        this.data.department_uuid = whs.department_uuid;
      }

      let max = this.getStockCount(this.data.warehouse_uuid);

      if (parseFloat(this.data.product.quantity) <= 0) {
        this.maxQtyError = this.$t(
          "stock_management.errors.quantity_cannot_be_zero"
        );
        this.saveButtonPressed = false;
        return false;
      } else if (parseFloat(this.data.product.quantity) > parseFloat(max)) {
        this.maxQtyError = this.$t(
          "stock_management.errors.quantity_cannot_be_greater",
          { max: max }
        );
        this.saveButtonPressed = false;
        return false;
      } else {
        this.maxQtyError = "";
      }

      if (!partnerValidate) {
        errorMessage(
          this,
          this.$t("stock_management.errors.partner_is_required")
        );
        this.saveButtonPressed = false;
        return false;
      }

      if (partnerValidate && !this.$v.data.$invalid) {
        if (typeof callback == "function") {
          callback(context, backPath);
        }
      }
    },
    save() {
      if (this.saveButtonPressed) {
        return;
      }
      this.saveButtonPressed = true;
      this.prepareSave(this.actions.save, this, this.config.list_url);
    },
    saveAndNew() {
      if (this.saveButtonPressed) {
        return;
      }
      this.saveButtonPressed = true;
      this.prepareSave(
        this.actions.saveAndNew(this).then(response => {
          this.product = {
            name: null
          };

          this.$set(this.data, "product", this.product);
          this.$set(this.data, "notes", null);
          this.$set(this, "productBarcodeSearch", '');
          this.$set(this, "selectedProductBarcode", null);
          this.$set(this, "productNameSearch", '');
          this.$set(this, "selectedProductName", null);
          this.$set(this, "stock_product_info", null);
          this.$set(this, "maxQtyError", '');

          if(response){
            this.onPartnerSelected(selected_partner);
            this.$set(this.data, "batch_uuid", response.batch_uuid);
            this.$refs['partner'].$refs['search'].$refs.input.disabled = true;
          }
          this.saveButtonPressed = false;
        }).catch((e) => {
          this.saveButtonPressed = false;
          console.log(e);
          errorMessage(this,'Error on saving data');
        })
      );
    },
    newObject() {
      let addUrl = "";
      this.$router
        .push({
          path: addUrl,
        })
        .catch((e) => {
          window.location.href = addUrl;
        });
    },
    onlyPrice(event) {
      return only_price(event);
    },
    onProductNameSelected(data) {
      if (data && data.value) {
        this.data.product = this.products.find((p) => p.uuid === data.value);

        this.onPartnerSelected(this.data.product.partner)

        this.productBarcodeSearch = {
          label: this.data.product.barcode,
          value: this.data.product.uuid,
        };
        this.stock_product_info = null;
      }
    },
    onPartnerSelected(data) {
      this.data.product.partner = data;
      this.addNewPartner = !this.data.product?.partner?.uuid ?? "edit";
      this.partnerInfo = data;
    },
    onPartnerChanged(data) {
      if (!this.partnerInfo) {
        this.partnerInfo = {};
      }

      if (data && data.name !== this.partnerInfo.name) {
        this.addNewPartner = data.name ? "add" : "";
        this.partnerInfo.name = data.name;
      }

      if (this.addNewPartner === "add") {
        this.partnerInfo = { name: data?.name };
      }

      if (!data) {
        this.partnerInfo = null;
      }
    },
    showPartnerDialog(partner) {
      this.showPartnersModal = true;
      this.partnersModalData = partner ?? {};
      this.partnersModalData.action = this.addNewPartner;
    },
    onPartnerDialogClosed(data) {
      this.showPartnersModal = false;

      if (data && data.name) {
        this.addNewPartner = "edit";
        this.data.product.partner = data;
        this.partnerName = data;
      }
    },
    contractClick() {
      this.showContractSelectDialog = true;
    },
    onContinueFromContractSelector(selectedContracts) {
      this.showContractSelectDialog = false;
      this.selectedContracts = selectedContracts;

      if (selectedContracts.length > 0) {
        this.data.warehouse_uuid = null;
        this.data.product = {
          uuid: selectedContracts[0].product_uuid,
          name: selectedContracts[0].product_name,
          barcode: selectedContracts[0].product_barcode,
          code: selectedContracts[0].product_code,
          product_type_uuid: selectedContracts[0].product_type_uuid,
          measure_unit_id: selectedContracts[0].measure_unit_id,
          contract_uuid: selectedContracts[0].uuid,
          partner: {
            uuid: selectedContracts[0].partner_uuid,
            name: selectedContracts[0].partner_name,
          },
        };
        this.productNameSearch = {
          value: selectedContracts[0].product_uuid,
          label: selectedContracts[0].product_name,
        };
        this.partnerInfo = {
          uuid: selectedContracts[0].partner_uuid,
          name: selectedContracts[0].partner_name,
        };
      }
    },
    clearSelectedContracts() {
      this.selectedContracts = [];
    },
    onWarehouseChanged(value) {
      if (this.selectedContracts.length > 0 && value) {
        const selectedWarehouse = this.warehouses.find((w) => w.uuid === value);
        if (selectedWarehouse) {
          if (
            this.selectedContracts[0].legal_entity_uuid &&
            this.selectedContracts[0].legal_entity_uuid !== selectedWarehouse.legal_entity_uuid
          ) {
            errorMessage(this, "Depozitul selectat nu aparține aceleiași entități juridice ca și contractul selectat!");
            this.data.warehouse_uuid = null;
            return;
          }
        }
      }
    },
    getStockCount(warehouse_uuid) {
      let stockItem =
        this.data.product && this.data.product.stock_list
          ? this.data.product.stock_list.find(
              (whs) => whs.warehouse_uuid === warehouse_uuid
            )
          : null;

      if (stockItem) {
        return stockItem.quantity;
      }

      return 0;
    },
  },
  mounted() {
    this.data.sales_date = moment().format(this.getDefaultDateFormat);
    this.getProductTypes({
      pagination: 0,
    });
    this.getWarehouses({
      enablePagination: 0,
      limit: 1000,
      filter: [["is_active", "=", 1]],
    });
    this.loadData();
    this.actions.handleParams(this);
  },
  watch: {
    productTypes: {
      handler: function (newVal) {
        this.product_types = Object.keys(newVal).map((key) => {
          return newVal[key];
        });
      },
      deep: true,
    },
    $route: {
      handler: function (newVal) {
        this.actions.handleParams(this);
      },
      deep: true,
    },
    permission: {
      handler: function (newVal) {
        this.permissionModule = newVal;
      },
      deep: true,
    },
    "data.price_net": (data) => {},
    data: {
      handler: (data) => {},
      deep: true,
    },
    "data.warehouse_uuid": {
      handler: function (newVal) {
        this.getProducts({
          warehouse_uuid: newVal,
          limit: 999,
          has_stock: 1,
          enablePagination: 0,
          resource_type: 'light',
          filter: [["is_active", "=", 1]],
        });
      },
      deep: true,
    },
    "data.product.quantity": {
      handler: function (newVal) {
        if (
          this.data.product.uuid &&
          this.data.warehouse_uuid &&
          this.data.product.quantity > 0
        ) {
          this.getStockProductInfo({
            product_uuid: this.data.product.uuid,
            warehouse_uuid: this.data.warehouse_uuid,
            quantity: this.data.product.quantity,
          });
        }
      },
    },
    stockProductInfo: {
      handler: function (newVal) {
        this.stock_product_info = newVal;
      },
      deep: true,
    },
  },
  validations() {
    return {
      data: {
        product: {
          name: {
            required,
          },
          unit_price: {
            required,
          },
          quantity: {
            required,
          },
        },
        warehouse_uuid: {
          required,
        },
        sales_date: {
          required,
        },
      },
    };
  },
};
</script>

<style scoped>
.textarea-2-lines {
  height: calc(1.5em * 2 + 0.75em + 2px);
}

.a-icon {
  position: relative;
  top: -26px;
  left: +40px;
}

.fyi-price {
  font-size: 10px;
}

.mu {
  position: relative;
  left: 0%;
  top: 39px;
}

.font-small {
  font-size: 12px;
}

.float-right {
  float: right;
  position: relative;
  top: -40px;
  left: 30px;
}
</style>
